import {Select} from '@wandb/weave/components/Form/Select';
import React, {useCallback, useEffect, useState} from 'react';
import {type InputActionMeta} from 'react-select';

import {useDebounceState} from '../../../util/hooks';
import {
  type ProjectOption,
  useCrossProjectOptions,
} from './useCrossProjectOptions';

type ProjectPickerProps = {
  selectedProject: ProjectOption | null;
  onSelectProject: (project: ProjectOption | null) => void;
  urlProjectName: string | null;
  onResolutionComplete: (result: ProjectResolutionResult) => void;
  onSearchInputChange: (searchInput: string) => void;
};

export type ProjectResolutionResult = {
  query: string;
  options: ProjectOption[];
  loadFailed: boolean;
};

export const ProjectPicker = ({
  selectedProject,
  onSelectProject,
  urlProjectName,
  onResolutionComplete,
  onSearchInputChange,
}: ProjectPickerProps) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [, debouncedQuery, setSearchInput] = useDebounceState('', 300);
  const queryString = urlProjectName ?? debouncedQuery;
  const {loading, options, loadFailed} = useCrossProjectOptions(queryString);

  const onInputChange = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        onSearchInputChange(newValue);
        setSearchInput(newValue);
      }
    },
    [onSearchInputChange, setSearchInput]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && menuIsOpen) {
        e.stopPropagation(); // prevent ESC from closing the drawer
        setMenuIsOpen(false);
      }
    },
    [menuIsOpen]
  );

  useEffect(() => {
    if (urlProjectName == null || queryString !== urlProjectName || loading) {
      return;
    }
    onResolutionComplete({
      query: urlProjectName,
      options,
      loadFailed,
    });
  }, [
    loadFailed,
    loading,
    onResolutionComplete,
    options,
    queryString,
    urlProjectName,
  ]);

  return (
    // `relative z-10` ensures select menu renders above sibling contents
    <div className="relative z-10">
      <label
        htmlFor="cross-project-picker"
        className="mb-4 block font-semibold">
        Source project
      </label>
      <Select<ProjectOption>
        inputId="cross-project-picker"
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        getOptionValue={o => o.internalId}
        getOptionLabel={o => `${o.entityName}/${o.name}`}
        options={options}
        value={selectedProject}
        onChange={onSelectProject}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        isLoading={loading}
        placeholder="Select a project"
      />
    </div>
  );
};
